import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchDashboardInitialState,
  saveLayout,
  updateLayout,
  createWidget,
  cloneWidget
} from "../redux/dashboardSlice.js";
import { fetchDatasourcesForDashboard } from "../redux/datasourceSlice.js";

const detectLayoutChange = (layout1, layout2) => {
  const map2 = Object.fromEntries(layout2.map(l => [l.i, l]));
  return layout1.reduce((changedIds, l) => {
    const m = map2[l.i];
    if (!m || l.x !== m.x || l.y !== m.y || l.w !== m.w || l.h !== m.h) {
      changedIds.push(l.i);
    }
    return changedIds;
  }, []);
};

export const useDashboardLayout = (props) => {
  const dispatch = useDispatch();
  const {editing, containerRef, projectId, dashboardId, colorScheme, datasetContainer, asPdf, showProfilePics, widgetSettings, disableRefresh, datasourceV2Enabled, datasourceV2Editable} = props;
  const widgets = useSelector((state) => state.dashboard.widgets);
  const layout = useSelector(state => state.dashboard.layout);
  const savedLayout = useSelector(state => state.dashboard.savedLayout);
  const hidden_items = useSelector(state => state.dashboard.hidden_items);
  const [visibleItems, setVisibleItems] = useState(new Set());
  
  useEffect(() => {
    dispatch(fetchDashboardInitialState({
      projectId: projectId,
      dashboardId: dashboardId,
      dashboardType: document.querySelector('#dashboard-type')?.getAttribute('data-dashboard-type'),
      hideRefresh: document.querySelector('#hide-refresh')?.getAttribute('data-hide-refresh'),
      colorScheme: colorScheme,
      datasetContainer: datasetContainer,
      asPdf: asPdf,
      showProfilePics: showProfilePics,
      widgetSettings: widgetSettings,
      disableRefresh: disableRefresh,
      datasourceV2Enabled: datasourceV2Enabled,
      datasourceV2Editable: datasourceV2Editable
    }));
    // Load centralized datasource data (skipped for public dashboards which have pre-injected widgetSettings)
    if (!widgetSettings) {
      dispatch(fetchDatasourcesForDashboard({ dashboardId, projectId }));
    }
  }, [dispatch])

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .map(entry => entry.target.dataset.id);

      if (visible.length === 0) return;

      setVisibleItems(prev => {
        const next = new Set([...prev, ...visible]);
        if (next.size === prev.size) return prev;
        return next;
      });
    }, { root: null, threshold: 0.05 });

    const items = containerRef.current.querySelectorAll('[data-id]');
    items.forEach(item => observer.observe(item));

    return () => items.forEach(item => observer.unobserve(item));
  }, [Object.keys(widgets)]);

  const isItemVisible = useCallback((id) => {
    const queryId = id.startsWith('widget') ? id : `widget-${id}`;
    return visibleItems.has(queryId);
  }, [visibleItems])

  const editable = useMemo(() => {
    return editing && Object.keys(savedLayout).length === 0;
  }, [editing, Object.keys(savedLayout)])

  const lastSavedLayoutRef = useRef(null);

  const getLayoutKey = useCallback((items = []) =>
    items.reduce((key, { i, x, y, w, h }) =>
      i === '__dropping-elem__' ? key : `${key}${i}:${x},${y},${w},${h}|`, ''
    ), []);

  const revertAutoSizeLayout = useCallback(() => {
    if (!editable) return;

    const newLayout = Object.values(layout).map((l) => {
      if (l.autoResizePreviousH == null) return l;
      const { autoResizePreviousH, ...item } = l;
      return { ...item, h: autoResizePreviousH };
    });

    const hasAutoResizedItems = Object.values(layout).some((l) => l.autoResizePreviousH != null);
    if (hasAutoResizedItems) {
      dispatch(updateLayout(newLayout));
    }
  }, [editable, layout]);

  useEffect(() => revertAutoSizeLayout(), [editable, dispatch]);

  const onLayoutChange = useCallback((newLayout, allLayouts) => {
    if (!editable) return;

    const cleanLg = allLayouts.lg.filter((l) => l.i !== '__dropping-elem__');
    const nextLayoutKey = getLayoutKey(cleanLg);

    if (nextLayoutKey === lastSavedLayoutRef.current) return;

    // We have hidden elements which trigger layout change on grid layout.
    // Preventing their removal
    const visibleLayout = Object.fromEntries(
      Object.entries(layout).filter(([id]) => !hidden_items.includes(Number(id)))
    );

    const changedIds = detectLayoutChange(Object.values(visibleLayout), cleanLg);
    if (changedIds.length > 0) {
      lastSavedLayoutRef.current = nextLayoutKey;

      // Need to preserve dragging state, else everything is draggable by default.
      const newLayout = cleanLg.map((l) => {
        return { ...l, isDraggable: visibleLayout[l.i.split('-')?.[1]]?.isDraggable || false}
      })
      // Need to update before save or else it gonna have a delay update, in frontend, the dragging Widget will go back to its old place then snap to the new place after the request
      dispatch(updateLayout(newLayout));
      dispatch(saveLayout());
    }
  }, [layout, hidden_items]);
  
  const onDrop = useCallback(async(layout, layoutItem, event) => {
    try {
      const type = event.dataTransfer.getData('widget-type');
      const existingId = event.dataTransfer.getData('existing-widget-id');
      const res = existingId
        ? await dispatch(cloneWidget({widgetId: existingId, layoutItem}))
        : await dispatch(createWidget({type, layoutItem}));

      const newLayoutItem = res?.payload?.layout;
      const newLayout = layout
        .filter((li) => li.i !== '__dropping-elem__')
        .map((li) => ({
          ...((li.i === `widget-${existingId}`) ? newLayoutItem : li)
        }));

      if (!newLayout.some((li) => li.i === newLayoutItem.i)) {
        newLayout.push(newLayoutItem);
      }

      lastSavedLayoutRef.current = getLayoutKey(newLayout);

      dispatch(updateLayout(newLayout));
      await dispatch(saveLayout());
    } catch (e) {
      alert(I18n.t('widget_creation_failed'), e);
    }
  }, [layout]);

  const onDrag = useCallback((layout, oldItem, newItem, placeholder, event, element) => {
    // Auto-scroll when dragging near viewport edges
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const mouseY = event.clientY;
    
    const scrollThreshold = 100; // pixels from edge
    const scrollSpeed = 10;
    
    if (mouseY < scrollThreshold) {
      // Scroll up
      window.scrollBy(0, -scrollSpeed);
    } else if (mouseY > viewportHeight - scrollThreshold) {
      // Scroll down  
      window.scrollBy(0, scrollSpeed);
    }
  }, []);
  
  
  const finalLayouts = useMemo(() => {
    const original = Object.values(layout) || [];

    const desktop = original.map(item => ({
      ...item,
      isDraggable: item.isDraggable || false
    }));

    const mobile = original.map((item, index) => {
      const w = 6;
      const x = (index % 2) * w;
      const y = Math.floor(index / 2) * item.h;
      return {...item, x, y, w, isDraggable: item.isDraggable || false};
    });

    return {lg: desktop, sm: mobile};
  }, [layout]);

  return { onLayoutChange, onDrop, onDrag, isItemVisible, editable, finalLayouts };
}